/**
 * Anpassningsbart tilläggsavtal till bostadshyresavtal (ADR-0061).
 *
 * A blank frame connected to a bostadshyresavtal: parties, an
 * "Avtalskoppling" pointing at the parent contract, then whatever custom
 * sections the user writes, and an optional signature section. No form
 * number; the title is the user's own.
 */
import { customSectionsBlockDefinition } from '../common/custom-sections.js';
import { partyDetailsBlock } from '../common/fields.js';
import { letterhead as commonLetterhead } from '../common/shared.js';
import { signingBlockDefinition } from '../common/signing.js';
import { propertySlot, slotText, textVal } from '../common/slots.js';
import { rows, sectionBlock } from '../bostadshyra/kit.js';
const K = 'clean_tillagg_bostad';
export const DEFAULT_DOCUMENT_TITLE = 'Tilläggsavtal till bostadshyresavtal';
export const documentTitle = (values) => slotText(values.document_title) ?? DEFAULT_DOCUMENT_TITLE;
/** The clean letterhead: brand marks and the user's document title, no form number. */
export const letterhead = {
    blockKey: `${K}.letterhead`,
    version: 'v1',
    plane: 'shared',
    status: 'published',
    language: 'sv-SE',
    title: 'Sidhuvud',
    summary: 'Repeating page header: brand marks and the configurable document title.',
    slots: [
        {
            key: 'document_title',
            label: 'Dokumenttitel',
            type: 'text',
            required: true,
            default: DEFAULT_DOCUMENT_TITLE,
            help: 'Visas överst i dokumentet och blir avtalets namn.',
        },
    ],
    compile: (input, ctx) => commonLetterhead.compile({ title: documentTitle(input) }, ctx),
};
export const hyresvard = partyDetailsBlock({
    blockKey: `${K}.hyresvard`,
    variant: 'inline',
    ref: 'hyresvard',
    title: 'Hyresvärd',
    summary: 'The landlord: name and organisation number.',
    groups: [
        {
            role: 'lessor',
            ordinal: 0,
            fields: [
                { key: 'landlord_name', label: 'Namn', field: 'name', required: true },
                { key: 'landlord_org_number', label: 'Organisationsnummer', field: 'legalId' },
            ],
        },
    ],
});
export const hyresgast = partyDetailsBlock({
    blockKey: `${K}.hyresgast`,
    variant: 'inline',
    ref: 'hyresgast',
    title: 'Hyresgäst',
    summary: 'The tenant and, when named, the co-tenant: name and id number.',
    groups: [
        {
            role: 'lessee',
            ordinal: 0,
            fields: [
                { key: 'tenant_name', label: 'Namn', field: 'name', required: true },
                { key: 'tenant_id_number', label: 'Personnr / Org.nr', field: 'legalId' },
            ],
        },
        {
            role: 'lessee',
            ordinal: 1,
            subheading: 'Medhyresgäst',
            showWhenAnyOf: ['cotenant_name'],
            fields: [
                { key: 'cotenant_name', label: 'Namn (medhyresgäst)', field: 'name' },
                { key: 'cotenant_id_number', label: 'Personnr / Org.nr (medhyresgäst)', field: 'legalId' },
            ],
        },
    ],
});
const FROM_PROPERTY = 'Förifylls från hyresavtalets fastighet.';
export const avtalskoppling = sectionBlock({
    blockKey: `${K}.avtalskoppling`,
    ref: 'avtalskoppling',
    title: 'Avtalskoppling',
    summary: 'The bostadshyresavtal this addendum belongs to: contract number, property and apartment number.',
    plane: 'shared',
    slots: [
        {
            key: 'contract_number',
            label: 'Hyreskontrakt nr',
            type: 'text',
            required: true,
            derivedFromParent: true,
            help: 'Numret på det bostadshyresavtal som tillägget avser. Förifylls från avtalet.',
        },
        propertySlot('property_designation', 'Fastighetsbeteckning', 'designation', { help: FROM_PROPERTY }),
        propertySlot('property_municipality', 'Kommun', 'municipality', { help: FROM_PROPERTY }),
        propertySlot('property_address', 'Postadress', 'address', { type: 'textarea', help: FROM_PROPERTY }),
        { key: 'apartment_official_number', label: 'Officiellt lägenhetsnummer', type: 'text', help: 'Förifylls från hyresavtalet.' },
    ],
    body: (input) => rows([
        ['Hyreskontrakt nr', [textVal(input, 'contract_number')]],
        ['Fastighetsbeteckning', [textVal(input, 'property_designation')]],
        ['Kommun', [textVal(input, 'property_municipality')]],
        ['Postadress', [textVal(input, 'property_address')]],
        ['Officiellt lägenhetsnummer', [textVal(input, 'apartment_official_number')]],
    ]),
});
const INCLUDE_SIGNING_SLOT = {
    key: 'include_signing',
    label: 'Inkludera signering',
    type: 'boolean',
    default: true,
    help: 'Av: dokumentet exporteras utan underskrift. På: undertecknas av parterna.',
};
export const underskrifter = signingBlockDefinition({
    blockKey: `${K}.underskrifter`,
    summary: 'Optional signature panels for landlord, tenant and (when named) co-tenant; no execution statement.',
    defaults: { ref: 'underskrift', title: 'Underskrifter' },
    slots: [INCLUDE_SIGNING_SLOT],
});
export const customSections = customSectionsBlockDefinition(`${K}.custom-sections`);
export const CLEAN_TILLAGG_BOSTAD_BLOCKS = [letterhead, hyresvard, hyresgast, avtalskoppling, underskrifter, customSections];
//# sourceMappingURL=blocks.js.map